import { ApolloLink, Observable } from '@apollo/client';
import type { FetchResult, Operation, NextLink } from '@apollo/client';

import { DEFAULT_HTTP_TIMEOUT_MS } from './constants';

/**
 * Apollo Link that adds per-operation timeout support via AbortController.
 *
 * Default timeout is DEFAULT_HTTP_TIMEOUT_MS (30s). Individual operations
 * can override by passing `context: { timeout: <ms> }` in useQuery/useMutation.
 *
 * Cleanup is guaranteed on success, error, and unsubscribe to prevent
 * dangling timers and late aborts.
 */
export const timeoutLink = new ApolloLink((operation: Operation, forward: NextLink): Observable<FetchResult> => {
  const context = operation.getContext();
  const timeout = context.timeout ?? DEFAULT_HTTP_TIMEOUT_MS;
  const correlationId: string | undefined = context.correlationId;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    const reason = new DOMException('Request timed out', 'TimeoutError');

    // Attach correlation ID to the abort reason for downstream error handlers
    if (correlationId) {
      (reason as DOMException & { correlationId?: string }).correlationId = correlationId;
    }

    controller.abort(reason);
  }, timeout);

  // Inject the abort signal into fetchOptions
  operation.setContext(({ fetchOptions = {} }: { fetchOptions?: RequestInit }) => ({
    fetchOptions: {
      ...fetchOptions,
      signal: controller.signal,
    },
  }));

  return new Observable<FetchResult>(observer => {
    const subscription = forward(operation).subscribe({
      next: value => {
        clearTimeout(timeoutId);
        observer.next(value);
      },
      error: err => {
        clearTimeout(timeoutId);
        observer.error(err);
      },
      complete: () => {
        clearTimeout(timeoutId);
        observer.complete();
      },
    });

    // Teardown: clear timer and abort on unsubscribe
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
      subscription.unsubscribe();
    };
  });
});
